// src/web/headTopOccluder.js
//
// FRONT-SKULL-CAP occluder — an interim cover for the front-top of the head so
// the temple arms stop showing on the forehead/front scalp when looking down.
//
// WHY THIS EXISTS: the face-landmark mesh stops at the hairline; it has no
// geometry over the scalp or beside the head. The temple arms travel back to
// ~z=-9.5cm (well behind the face mesh's -2.4cm rear edge), so when you look
// down they ride over the forehead/front scalp with nothing to occlude them.
//
// APPROACH (deliberately FRONT-HALF only): take the real forehead-top landmark
// ring and extrude a second ring UP (+Y) and BACK (-Z) to approximate the front
// scalp, then triangulate a band between the two rings. This covers exactly
// where the arms currently show. It stays at the front/top and does NOT reach
// the ears/sides — leaving that region free for the planned future work that
// renders the temple tips tucked behind the ears.
//
// It is posed by the SAME pinned matrix as the glasses/occluder, so it shares
// one depth frame (consistent occlusion, no fighting). The forehead ring uses
// LIVE landmarks (tracks the real head); the extruded ring is synthetic, sized
// by tunable params (?headtopup, ?headtopback) and viewable via the magenta
// debug toggle.

import {
  BufferGeometry, BufferAttribute, Mesh, MeshBasicMaterial, DoubleSide, Vector3
} from 'three';
import { CANONICAL_FACE_VERTICES } from './canonicalFace.js';

// Forehead-top ring, ordered from the right temple, across the top, to the left
// temple (so consecutive entries are neighbours for triangulation).
const FOREHEAD_RING = [356, 389, 251, 284, 332, 297, 338, 10, 109, 67, 103, 54, 21, 162, 127];

export class HeadTopOccluder {
  constructor() {
    const qs = new URLSearchParams(window.location.search);
    const num = (k, fb) => { const v = parseFloat(qs.get(k)); return Number.isFinite(v) ? v : fb; };

    // Extrusion of the synthetic back ring (canonical cm).
    this._up   = num('headtopup', 6.0);    // max up-rise (at the crown)
    this._back = num('headtopback', 9.0);  // back extent (follows the arm)
    this._depthBias = num('occbias', 0.0);

    // Precompute per-point up-fraction: the crown (high y) rises fully; the
    // temple-side points (low y) barely rise and instead trail straight BACK at
    // the arm's height — which is what actually covers the temple arm. Without
    // this, the cap covers only the crown and the arm shows below its front edge.
    const ringY = FOREHEAD_RING.map(i => CANONICAL_FACE_VERTICES[i*3+1]);
    const yMin = Math.min(...ringY), yMax = Math.max(...ringY);
    this._upFrac = ringY.map(y => (yMax > yMin) ? (y - yMin) / (yMax - yMin) : 1);

    const N = FOREHEAD_RING.length;
    // Vertices: front ring (N, live) + back ring (N, extruded). 2N total.
    this._N = N;
    this.positions = new Float32Array(N * 2 * 3);

    // Triangulate the band between front ring [0..N-1] and back ring [N..2N-1].
    const idx = [];
    for (let i = 0; i < N - 1; i++) {
      const f0 = i, f1 = i + 1, b0 = N + i, b1 = N + i + 1;
      idx.push(f0, b0, f1);     // tri 1
      idx.push(f1, b0, b1);     // tri 2
    }
    this.geometry = new BufferGeometry();
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    this.geometry.setIndex(idx);

    this.material = new MeshBasicMaterial({
      colorWrite: false, depthWrite: true, depthTest: true, side: DoubleSide
    });
    this._debugMaterial = new MeshBasicMaterial({
      color: 0xffaa00, colorWrite: true, transparent: true, opacity: 0.4,
      depthWrite: false, depthTest: true, side: DoubleSide
    });

    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.renderOrder = 0;
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.visible = false;

    this.enabled = true;
    this._ray = new Vector3();
  }

  /**
   * @param {Array<{x,y,z}>} landmarks  real normalized landmarks
   * @param {THREE.Matrix4} pinnedM     same matrix the glasses use
   * @param {THREE.PerspectiveCamera} camera
   */
  update(landmarks, pinnedM, camera) {
    if (!this.enabled || !landmarks || !pinnedM) { this.mesh.visible = false; return; }
    const cv = CANONICAL_FACE_VERTICES;
    const e = pinnedM.elements;
    const N = this._N;

    for (let k = 0; k < N; k++) {
      const li = FOREHEAD_RING[k];
      const lm = landmarks[li];
      if (!lm) { this.mesh.visible = false; return; }

      // FRONT ring: unproject the REAL landmark at the depth M gives its
      // canonical vertex (same hybrid scheme as the live occluder → consistent
      // depth with the glasses).
      const cx = cv[li*3], cy = cv[li*3+1], cz = cv[li*3+2];
      const depth = Math.abs(e[2]*cx + e[6]*cy + e[10]*cz + e[14]) + this._depthBias;
      const ndcX = lm.x * 2 - 1, ndcY = -(lm.y * 2 - 1);
      this._ray.set(ndcX, ndcY, 0.5).unproject(camera);
      this._ray.sub(camera.position).normalize();
      const t = -depth / this._ray.z;
      const fx = camera.position.x + this._ray.x * t;
      const fy = camera.position.y + this._ray.y * t;
      const fz = camera.position.z + this._ray.z * t;
      this.positions[k*3+0] = fx;
      this.positions[k*3+1] = fy;
      this.positions[k*3+2] = fz;

      // BACK ring: the canonical forehead vertex extruded BACK (-Z) by _back and
      // UP (+Y) by _up scaled per-point — crown rises fully, temple sides barely
      // rise and trail straight back at the arm's height (covering the arm).
      const ex = cx, ey = cy + this._up * this._upFrac[k], ez = cz - this._back;
      const wx = e[0]*ex + e[4]*ey + e[8]*ez + e[12];
      const wy = e[1]*ex + e[5]*ey + e[9]*ez + e[13];
      const wz = e[2]*ex + e[6]*ey + e[10]*ez + e[14];
      const bi = N + k;
      this.positions[bi*3+0] = wx;
      this.positions[bi*3+1] = wy;
      this.positions[bi*3+2] = wz;
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeVertexNormals();
    this.geometry.computeBoundingSphere();
    this.mesh.visible = true;
  }

  setEnabled(on) { this.enabled = on; if (!on) this.mesh.visible = false; }
  setDebugVisible(on) {
    this.mesh.material = on ? this._debugMaterial : this.material;
    this.mesh.renderOrder = on ? 20 : 0;
  }
  hide() { this.mesh.visible = false; }
  dispose() { this.geometry.dispose(); this.material.dispose(); }
}
